import { Box, HStack, Tooltip, VStack, Text, useToast } from '@chakra-ui/react';
import { djs } from '@duke-hq/djs';
import { Copy, TriangleAlert } from 'lucide-react';
import { iconSize } from '@duke-hq/ui/constants/scalable';

export type ValueTypes = string | null | undefined | Date;

const displayValue = (value: ValueTypes) => {
  if (value instanceof Date) {
    const date = djs(value);

    if (date.isValid() === false) {
      return '-';
    }
    return date.format('DD-MM-YYYY');
  }

  return value || '-';
};

/**
 * reusable component to display a single field, usually inside a grid
 */
export function SingleField({
  name,
  value,
  copyToClipboard = false,
  warning,
  ...rest
}: {
  name: React.ReactNode;
  value: ValueTypes | ValueTypes[];
  copyToClipboard?: boolean;
  warning?: string | null;
}) {
  const toast = useToast();

  const handleCopyClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    void navigator.clipboard.writeText(
      Array.isArray(value) ? value.join(', ') : (value?.toString() ?? ''),
    );

    toast({
      title: 'Copied to clipboard',
      status: 'success',
      duration: 3000,
    });
  };

  return (
    <VStack alignItems="flex-start" gap={0} {...rest}>
      <Text textStyle="sm" color="fg.muted" mb={0} mt={2} fontSize="xs">
        {name}
      </Text>
      {Array.isArray(value) ? (
        value.map((v, index) => (
          <Box key={`${index}-${v?.toString()}`}>
            <Text mt={0}>{v?.toString()}</Text>
          </Box>
        ))
      ) : (
        <HStack spacing="2">
          <Text mt={0}>{displayValue(value)}</Text>
          {warning != null && warning !== '' ? (
            <Tooltip label={warning} hasArrow placement="top">
              <Box as="span" display="inline-flex" color="orange.500" cursor="help">
                <TriangleAlert size={iconSize.sm} aria-label={warning} />
              </Box>
            </Tooltip>
          ) : null}
          {copyToClipboard ? (
            <Copy size={iconSize.sm} cursor="pointer" onClick={handleCopyClick} />
          ) : null}
        </HStack>
      )}
    </VStack>
  );
}
